import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Clock, Loader2, CheckCircle2, XCircle, ArrowLeft, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const timeLabel = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
const dayKey = (iso) => new Date(iso).toDateString();

export default function BookingManage() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | cancelled | error
  const [mode, setMode] = useState("view"); // view | picker
  const [slots, setSlots] = useState([]);
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  useEffect(() => {
    api.get(`/bookings/manage/${id}`)
      .then(({ data }) => { setBooking(data); setStatus("ok"); })
      .catch((err) => setStatus(err?.response?.status === 404 ? "notfound" : "error"));
  }, [id]);

  const loadSlots = () => {
    setBusy(true);
    api.get("/bookings/slots")
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => toast.error("Could not load times."))
      .finally(() => setBusy(false));
  };

  useEffect(() => { if (mode === "picker" && !slots.length) loadSlots(); }, [mode]); // eslint-disable-line

  const daysMap = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      const key = dayKey(s.slot_iso);
      if (!map.has(key)) map.set(key, { key, slots: [] });
      map.get(key).slots.push(s);
    });
    return map;
  }, [slots]);

  const monthCells = useMemo(() => {
    const first = new Date(viewMonth);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push({ key: d.toDateString(), inMonth: d.getMonth() === viewMonth.getMonth(), dayNum: d.getDate() });
    }
    return cells;
  }, [viewMonth]);

  const monthBounds = useMemo(() => {
    if (!slots.length) return null;
    const first = new Date(slots[0].slot_iso);
    const last = new Date(slots[slots.length - 1].slot_iso);
    return {
      min: new Date(first.getFullYear(), first.getMonth(), 1),
      max: new Date(last.getFullYear(), last.getMonth(), 1),
    };
  }, [slots]);

  const activeDay = selectedDayKey ? daysMap.get(selectedDayKey) : null;

  const cancel = async () => {
    if (!window.confirm("Cancel this diagnostic? Kalib and John will be notified.")) return;
    setBusy(true);
    try {
      await api.post(`/bookings/manage/${id}/cancel`);
      setStatus("cancelled");
      toast.success("Your diagnostic has been cancelled.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not cancel.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const submitReschedule = async () => {
    if (!selectedSlot) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/manage/${id}/reschedule`, { slot_iso: selectedSlot.slot_iso });
      setBooking(data.booking);
      setMode("view");
      setSelectedSlot(null);
      setSelectedDayKey(null);
      toast.success("Rescheduled — a confirmation is on its way.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not reschedule.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#eae7de] text-[#1e3d3a]">
      <header className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
        <Link to="/" data-testid="manage-home" className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#1e3d3a]/60 hover:text-[#1e3d3a] transition-colors duration-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Elevare Solutions
        </Link>
        <span className="text-[10px] uppercase tracking-[0.28em] text-[#c9a961]">Manage booking</span>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-24">
        {status === "loading" && (
          <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-10 flex items-center justify-center gap-3 text-sm text-[#1e3d3a]/55">
            <Loader2 className="h-4 w-4 animate-spin" /> Looking up your booking…
          </div>
        )}

        {status === "notfound" && (
          <div data-testid="manage-notfound" className="rounded-2xl border border-[#b23a48]/25 bg-white p-10 text-center">
            <AlertTriangle className="h-8 w-8 text-[#b23a48] mx-auto" strokeWidth={1.4} />
            <h1 className="mt-4 font-head text-2xl text-[#1e3d3a]">We couldn&rsquo;t find that booking</h1>
            <p className="mt-3 text-sm text-[#1e3d3a]/65 max-w-md mx-auto">
              The link may have expired or the diagnostic may already have been cancelled. Book a fresh time on the main site.
            </p>
            <Link to="/#contact" className="mt-6 inline-flex rounded-full bg-[#1e3d3a] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
              Book a new diagnostic
            </Link>
          </div>
        )}

        {status === "cancelled" && (
          <div data-testid="manage-cancelled" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-10 text-center">
            <CheckCircle2 className="h-8 w-8 text-[#4b7a3a] mx-auto" strokeWidth={1.4} />
            <h1 className="mt-4 font-head text-2xl text-[#1e3d3a]">Diagnostic cancelled</h1>
            <p className="mt-3 text-sm text-[#1e3d3a]/65 max-w-md mx-auto">
              We&rsquo;ve let Kalib and John know. Whenever you&rsquo;re ready to reschedule, we&rsquo;re here.
            </p>
            <Link to="/#contact" className="mt-6 inline-flex rounded-full bg-[#1e3d3a] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
              Book a new time
            </Link>
          </div>
        )}

        {status === "ok" && booking && (
          <div data-testid="manage-view" className="space-y-6">
            <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-8">
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#c9a961]">Your diagnostic</span>
              <div className="mt-3 flex items-center gap-3">
                <CalendarDays className="h-6 w-6 text-[#a3823f]" strokeWidth={1.4} />
                <h1 data-testid="manage-slot" className="font-head text-2xl sm:text-3xl text-[#1e3d3a]">{booking.pretty}</h1>
              </div>
              <p className="mt-3 text-sm text-[#1e3d3a]/60">
                Booked under <strong>{booking.name}</strong> · {booking.email}
                {booking.company ? <> · {booking.company}</> : null}
              </p>
              {booking.goal && (
                <p className="mt-3 text-sm text-[#1e3d3a]/55 italic border-l-2 border-[#c9a961] pl-3">&ldquo;{booking.goal}&rdquo;</p>
              )}

              {mode === "view" && (
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    data-testid="manage-reschedule-btn"
                    disabled={busy}
                    onClick={() => setMode("picker")}
                    className="flex-1 rounded-full bg-[#1e3d3a] text-white py-3 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60"
                  >
                    Reschedule
                  </button>
                  <button
                    type="button"
                    data-testid="manage-cancel-btn"
                    disabled={busy}
                    onClick={cancel}
                    className="rounded-full border border-[#b23a48]/30 text-[#b23a48] py-3 px-6 text-sm font-semibold hover:bg-[#b23a48] hover:text-white transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    Cancel booking
                  </button>
                </div>
              )}
            </div>

            <AnimatePresence>
              {mode === "picker" && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[10px] uppercase tracking-[0.28em] text-[#c9a961]">Pick a new time</span>
                    <button
                      type="button"
                      data-testid="manage-picker-close"
                      onClick={() => { setMode("view"); setSelectedSlot(null); setSelectedDayKey(null); }}
                      className="text-xs text-[#1e3d3a]/55 hover:text-[#1e3d3a] transition-colors duration-300"
                    >
                      Cancel
                    </button>
                  </div>

                  {busy && !slots.length ? (
                    <div className="py-8 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin text-[#c9a961]" />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <button
                          type="button"
                          onClick={() => setViewMonth((m) => {
                            const next = new Date(m.getFullYear(), m.getMonth() - 1, 1);
                            return monthBounds && next < monthBounds.min ? monthBounds.min : next;
                          })}
                          disabled={!!monthBounds && viewMonth <= monthBounds.min}
                          className="h-8 w-8 flex items-center justify-center rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-30 transition-colors duration-300"
                          data-testid="manage-prev-month"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <div className="font-head text-lg text-[#1e3d3a]">
                          {viewMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                        </div>
                        <button
                          type="button"
                          onClick={() => setViewMonth((m) => {
                            const next = new Date(m.getFullYear(), m.getMonth() + 1, 1);
                            return monthBounds && next > monthBounds.max ? monthBounds.max : next;
                          })}
                          disabled={!!monthBounds && viewMonth >= monthBounds.max}
                          className="h-8 w-8 flex items-center justify-center rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-30 transition-colors duration-300"
                          data-testid="manage-next-month"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50 mb-1">
                        {WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {monthCells.map((c) => {
                          const info = daysMap.get(c.key);
                          const hasSlots = !!info;
                          const openCount = hasSlots ? info.slots.filter((s) => !s.taken).length : 0;
                          const disabled = !hasSlots || openCount === 0;
                          const isActive = c.key === selectedDayKey;
                          return (
                            <button
                              key={c.key}
                              type="button"
                              data-testid={`manage-day-${c.key}`}
                              onClick={() => { if (!disabled) { setSelectedDayKey(c.key); setSelectedSlot(null); } }}
                              disabled={disabled}
                              className={`relative h-11 rounded-lg text-sm font-head tabular-nums transition-colors duration-300 border ${
                                isActive ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a] font-semibold" :
                                !disabled ? "border-[#1e3d3a]/12 text-[#1e3d3a] hover:border-[#c9a961]/60 hover:bg-[#c9a961]/6" :
                                "border-transparent text-[#1e3d3a]/25 cursor-not-allowed"
                              } ${!c.inMonth ? "opacity-40" : ""}`}
                            >
                              {c.dayNum}
                              {openCount > 0 && (
                                <span className={`absolute left-1/2 -translate-x-1/2 bottom-1.5 h-1 w-1 rounded-full ${isActive ? "bg-[#1e3d3a]" : "bg-[#c9a961]"}`} />
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {activeDay && (
                        <div className="mt-5">
                          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-3">
                            <Clock className="h-3.5 w-3.5" /> Times available
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {activeDay.slots.map((s) => {
                              const active = selectedSlot?.slot_iso === s.slot_iso;
                              return (
                                <button
                                  key={s.slot_iso}
                                  type="button"
                                  data-testid={`manage-slot-${s.slot_iso}`}
                                  disabled={s.taken}
                                  onClick={() => setSelectedSlot(s)}
                                  className={`rounded-full px-4 py-1.5 text-xs transition-colors duration-300 border ${
                                    active ? "bg-[#1e3d3a] text-white border-[#1e3d3a]" :
                                    s.taken ? "border-[#1e3d3a]/12 text-[#1e3d3a]/30 line-through cursor-not-allowed" :
                                    "border-[#1e3d3a]/20 text-[#1e3d3a] hover:border-[#c9a961]"
                                  }`}
                                >
                                  {timeLabel(s.slot_iso)}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        data-testid="manage-confirm-reschedule"
                        onClick={submitReschedule}
                        disabled={!selectedSlot || busy}
                        className="mt-6 w-full rounded-full bg-[#1e3d3a] text-white py-3 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-40 flex items-center justify-center gap-2"
                      >
                        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Rescheduling…</> :
                          selectedSlot ? <>Move to {new Date(selectedSlot.slot_iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {timeLabel(selectedSlot.slot_iso)}</> :
                          "Pick a new time"}
                      </button>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
}
